import { AsyncExportResponse, ExportParams, ItemType, ListResponseType, Pageable, ParamsWithFilters } from 'src/types';
import { ApiClient } from '.';
import {
  CompliantContainersResponse,
  DiagramsCardsResponse,
  LineChartsResponse,
  LineChartsType,
  PieChartsResponse,
} from './api-types/diagrams';
import {
  EarLocationsRequestParams,
  GeneratorsFilterType,
  GeneratorsResponse,
  RequestGeneratorsParams,
} from './api-types/generators';
import { dotNameKeyToObject, removeEmptyValues } from '../utils/string';
import qs from 'qs';

export class EarDiagramsApi extends ApiClient {
  getEarGeneratorsData({ page, pageSize, filters, additionalFilters }: RequestGeneratorsParams) {
    const q = dotNameKeyToObject(filters);
    if (q && additionalFilters) {
      q.ear = additionalFilters;
    }
    return this.requestPost<
      GeneratorsResponse,
      ParamsWithFilters<GeneratorsFilterType>
    >('ear-locations/list', {
      q,
      page: {
        num: page,
        size: pageSize,
      },
    });
  }
  async getGeneratorsFilterOptions({ filters, additionalFilters, column }: { filters: Partial<GeneratorsFilterType>, additionalFilters: EarLocationsRequestParams | null, column: string }) {
    const filterMap: { [key: string]: string } = {
      hauler: 'hauler.name',
      jurisdiction: 'jurisdiction.name',
      agency: 'agency.name',
      types: 'type.name',
      accountType: 'accountType.name',
      zone: 'zone.name',
      origin: 'origin.name',
      collectionSystem: 'collectionSystem.name',
      containerColorsGray: 'containerColors.gray',
      containerColorsBlue: 'containerColors.blue',
      containerColorsGreen: 'containerColors.green',
    };
    const filterColumn = filterMap[column] ?? column;
    const rawQuery = dotNameKeyToObject(filters);
    const q = rawQuery ? removeEmptyValues(rawQuery) : {};
    if (q && additionalFilters) {
      q.ear = additionalFilters;
    }
    
    const response = await this.requestPost<
      ListResponseType<ItemType> & Pageable,
      ParamsWithFilters<GeneratorsFilterType> & { column: string }
    >('locations/list/filter-options', {
      q,
      page: {
        num: 1,
        size: 100000,
      },
      column: filterColumn,
    });

    return response.data!.items;
  }

  getCommonCards(jurisdictionId?: number, typeIds?: string[]) {
    const numericTypeIds = typeIds?.map(id => Number(id));
    return this.requestGet<DiagramsCardsResponse>('ear-dashboard/Cards', {
      'q.jurisdictionId': jurisdictionId,
      ...(numericTypeIds && numericTypeIds.length > 0 && { 'q.typeIds': numericTypeIds }),
    }, {
      paramsSerializer: (params: any) => qs.stringify(params, { allowDots: true, arrayFormat: 'indices' }),
    });
  }

  getGeneralCards(jurisdictionId?: number, typeIds?: string[]) {
    const numericTypeIds = typeIds?.map(id => Number(id));
    return this.requestGet<DiagramsCardsResponse>('ear-dashboard/GeneralCards', {
      'q.jurisdictionId': jurisdictionId,
      ...(numericTypeIds && numericTypeIds.length > 0 && { 'q.typeIds': numericTypeIds }),
    }, {
      paramsSerializer: (params: any) => qs.stringify(params, { allowDots: true, arrayFormat: 'indices' }),
    });
  }

  getMOReCards(jurisdictionId?: number, typeIds?: string[]) {
    const numericTypeIds = typeIds?.map(id => Number(id));
    return this.requestGet<DiagramsCardsResponse>('ear-dashboard/MOReCards', {
      'q.jurisdictionId': jurisdictionId,
      ...(numericTypeIds && numericTypeIds.length > 0 && { 'q.typeIds': numericTypeIds }),
    }, {
      paramsSerializer: (params: any) => qs.stringify(params, { allowDots: true, arrayFormat: 'indices' }),
    });
  }

  getMcrCards(jurisdictionId?: number, typeIds?: string[]) {
    const numericTypeIds = typeIds?.map(id => Number(id));
    return this.requestGet<DiagramsCardsResponse>('ear-dashboard/McrCards', {
      'q.jurisdictionId': jurisdictionId,
      ...(numericTypeIds && numericTypeIds.length > 0 && { 'q.typeIds': numericTypeIds }),
    }, {
      paramsSerializer: (params: any) => qs.stringify(params, { allowDots: true, arrayFormat: 'indices' }),
    });
  }

  getPieChart(jurisdictionId?: number, filters?: Partial<GeneratorsFilterType>, typeIds?: string[]) {
    const numericTypeIds = typeIds?.map(id => Number(id));
    return this.requestGet<PieChartsResponse>('ear-dashboard/SlcpPieChart', {
      'q.jurisdictionId': jurisdictionId,
      ...(numericTypeIds && numericTypeIds.length > 0 && { 'q.typeIds': numericTypeIds }),
      ...filters,
    }, {
      paramsSerializer: (params: any) => qs.stringify(params, { allowDots: true, arrayFormat: 'indices' }),
    });
  }

  getNonPieChart(jurisdictionId?: number, filters?: Partial<GeneratorsFilterType>, typeIds?: string[]) {
    const numericTypeIds = typeIds?.map(id => Number(id));
    return this.requestGet<PieChartsResponse>(
      'ear-dashboard/SlcpNonPieChart',
      {
        'q.jurisdictionId': jurisdictionId,
        ...(numericTypeIds && numericTypeIds.length > 0 && { 'q.typeIds': numericTypeIds }),
        ...filters,
      }, {
        paramsSerializer: (params: any) => qs.stringify(params, { allowDots: true, arrayFormat: 'indices' }),
      }
    );
  }

  getSubjectLineChart(jurisdictionId?: number) {
    return this.requestGet<LineChartsResponse>(
      'ear-dashboard/SlcpSubjectLineChart',
      {
        'q.jurisdictionId': jurisdictionId,
      }, {
        paramsSerializer: (params: any) => qs.stringify(params, { allowDots: true, arrayFormat: 'indices' }),
      }
    );
  }

  getEARLineChart(jurisdictionId?: number, typeIds?: string[]) {
    const numericTypeIds = typeIds?.map(id => Number(id));
    return this.requestGet<LineChartsType>(
      'ear-dashboard/SlcpSubjectLineChart/highcharts',
      {
        'q.jurisdictionId': jurisdictionId,
        ...(numericTypeIds &&
          numericTypeIds.length > 0 && { 'q.typeIds': numericTypeIds }),
      }, {
        paramsSerializer: (params: any) => qs.stringify(params, { allowDots: true, arrayFormat: 'indices' }),
      }
    );
  }

  getSLCPLineChart(jurisdictionId?: number, filters?: Partial<GeneratorsFilterType>, typeIds?: string[]) {
    const numericTypeIds = typeIds?.map(id => Number(id));
    return this.requestGet<LineChartsType>(
      'ear-dashboard/SlcpComplianceLineChart/highcharts',
      {
        'q.jurisdictionId': jurisdictionId,
        ...(numericTypeIds &&
          numericTypeIds.length > 0 && { 'q.typeIds': numericTypeIds }),
        ...filters,
      }, {
        paramsSerializer: (params: any) => qs.stringify(params, { allowDots: true, arrayFormat: 'indices' }),
      }
    );
  }

  getSlcpCompliancePieChart(jurisdictionId?: number, filters?: Partial<GeneratorsFilterType>, typeIds?: string[]) {
    const numericTypeIds = typeIds?.map(id => Number(id));
    return this.requestGet<PieChartsResponse>(
      'ear-dashboard/SlcpCompliancePieChart',
      {
        'q.jurisdictionId': jurisdictionId,
        ...(numericTypeIds && numericTypeIds.length > 0 && { 'q.typeIds': numericTypeIds }),
        ...filters,
      }, {
        paramsSerializer: (params: any) => qs.stringify(params, { allowDots: true, arrayFormat: 'indices' }),
      }
    );
  }

  getGeneratorsNotCompliantByContainerPieChart(jurisdictionId?: number, filters?: Partial<GeneratorsFilterType>, typeIds?: string[]) {
    const numericTypeIds = typeIds?.map(id => Number(id));
    return this.requestGet<PieChartsResponse>(
      'ear-dashboard/SlcpNonComplianceContainersPieChart',
      {
        'q.jurisdictionId': jurisdictionId,
        ...(numericTypeIds && numericTypeIds.length > 0 && { 'q.typeIds': numericTypeIds }),
        ...filters,
      }, {
        paramsSerializer: (params: any) => qs.stringify(params, { allowDots: true, arrayFormat: 'indices' }),
      }
    );
  }

  getAsyncReportByType(path: string, params: ExportParams, exportType: string) {
    return this.requestPost<AsyncExportResponse, ExportParams>(
      `${path}/export/${exportType}?flowType=2`,
      params,
    );
  }

  getAsyncDashboardChartByType(path: string, type: string, params: ExportParams) {
    return this.getAsyncReportByType(`ear-dashboard/${path}/highcharts`, params, type );
  }

  getAsyncGeneratorsReportByType(params: ExportParams, additionalFilters: EarLocationsRequestParams | null, exportType: string) {
    if (params.q && additionalFilters) {
      params = { ...params, q: { ...params.q, ear: additionalFilters } };
    }
    return this.getAsyncReportByType('ear-locations', params, exportType );
  }

  getCompliantContainers(filterId: string, typeIds?: string[]) {
    const params = {
      q: {
        filter: { id: Number(filterId) },
        ...(typeIds && typeIds.length > 0 && { types: typeIds?.map(id => ({ id: Number(id) })) }),
      }
    }

    return this.requestGet<CompliantContainersResponse>(
      'ear-dashboard/SlcpCompliantContainers',
      params, {
        paramsSerializer: (params: any) => qs.stringify(params, { allowDots: true, arrayFormat: 'indices' }),
      }
    );
  }
}
